import { Head, Link, router } from "@inertiajs/react"
import { ArrowDown, ArrowUp, ArrowUpDown, Building2, Plus } from "lucide-react"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { BrandListActAsButton } from "@/components/admin/brand-list-act-as-button"
import { ListPagination } from "@/components/admin/list-pagination"
import { ListShell } from "@/components/admin/list-shell"
import { ListToolbar } from "@/components/admin/list-toolbar"
import { NewTenantDialog } from "@/components/admin/new-tenant-dialog"
import { BrandLiveBadge } from "@/components/brand-live-badge"
import { EmptyState } from "@/components/empty-state"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import { adminBrandPath, adminBrandsPath } from "@/routes"
import type { BrandsIndexProps } from "@/types/admin"

// Backend's `Admin::BrandsController::SORTABLE` allow-list: any column
// outside this set is silently coerced to the default `created_at`.
type SortableColumn = "name" | "created_at"

/**
 * Admin/Brands/Index — paginated, searchable brand list (OKS-15 AC2).
 *
 * URL is the single source of list state — `?q=…&page=…&created_from=…&
 * created_to=…&sort_by=…&sort_dir=…`. The toolbar's debounced search,
 * the sort headers, and the prev/next pagination all dispatch through
 * `router.get(currentPath, …)` with `preserveState`+`preserveScroll`+
 * `only:[…]` partial reload — Inertia replaces only the props that
 * changed; the AdminShell + AdminSidebar stay mounted.
 *
 * Five states per the v6 TRD §State + UX Spec §4 matrix:
 *   - loading — implicit during in-flight Inertia partial reload
 *     (the existing `<Table>` rows stay mounted; the offline banner
 *     does NOT fire here since browser is online)
 *   - empty (fresh) — `EmptyState` with the per-tenant fresh copy
 *   - empty (filter miss) — `EmptyState` with filter-miss copy + Clear
 *     filters button that re-routes to the path with no query params
 *   - error — read-side errors are vanishingly rare with Inertia's
 *     server-rendered page model (any 5xx hits the global error page);
 *     no inline retry rendered for the read path
 *   - offline — `<ListShell>`'s built-in `admin-offline-banner` via
 *     `useOnline`; toolbar search input disables; pagination disables
 *
 * Test IDs per the v6 contract: `admin-brands-list` (page region),
 * `admin-list-row` with `data-brand-id={id}`, plus the shared
 * `admin-list-search` / `admin-list-pagination` / `admin-list-empty` /
 * `admin-offline-banner` / `admin-list-sort-{column}` from the list
 * primitives.
 */
export default function AdminBrandsIndex({
  brands,
  pagination,
  filters,
  sort,
  can_create_brand,
}: BrandsIndexProps) {
  const { t, i18n } = useTranslation()

  const hasActiveFilters =
    filters.q.length > 0 ||
    filters.created_from.length > 0 ||
    filters.created_to.length > 0
  const isEmpty = brands.length === 0

  const visit = useCallback(
    (
      overrides: Partial<{
        q: string
        page: number
        sort_by: string
        sort_dir: string
      }>,
    ) => {
      const params: Record<string, string | number> = {
        q: filters.q,
        created_from: filters.created_from,
        created_to: filters.created_to,
        sort_by: sort.by,
        sort_dir: sort.dir,
        page: pagination.page,
        ...overrides,
      }
      // Strip empty-string params from the URL so the rendered URL
      // mirrors actual filter state (`/admin/brands` vs
      // `/admin/brands?q=&page=1&sort_by=created_at&sort_dir=desc`).
      const clean: Record<string, string | number> = {}
      for (const [k, v] of Object.entries(params)) {
        if (v !== "" && v !== null && v !== undefined) clean[k] = v
      }
      router.get(adminBrandsPath(), clean, {
        preserveState: true,
        preserveScroll: true,
        only: ["brands", "pagination", "filters", "sort"],
      })
    },
    [
      filters.q,
      filters.created_from,
      filters.created_to,
      sort.by,
      sort.dir,
      pagination.page,
    ],
  )

  const onSearch = useCallback((q: string) => visit({ q, page: 1 }), [visit])

  const onPageChange = useCallback((page: number) => visit({ page }), [visit])

  const onSortToggle = useCallback(
    (column: SortableColumn) => {
      const nextDir = sort.by === column && sort.dir === "desc" ? "asc" : "desc"
      visit({ sort_by: column, sort_dir: nextDir, page: 1 })
    },
    [sort.by, sort.dir, visit],
  )

  const onClearFilters = useCallback(() => {
    router.get(
      adminBrandsPath(),
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: ["brands", "pagination", "filters", "sort"],
      },
    )
  }, [])

  const dateFmt = new Intl.DateTimeFormat(i18n.language, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  return (
    <AppLayout>
      <Head title={t("admin.brands_list.title")} />
      <ListShell
        testId="admin-brands-list"
        title={t("admin.brands_list.title")}
        subtitle={t("admin.brands_list.subtitle_total", {
          count: pagination.total,
        })}
        action={
          can_create_brand ? (
            <NewTenantDialog
              kind="brand"
              trigger={
                <Button type="button" data-testid="admin-new-brand-button">
                  <Plus className="size-4" aria-hidden="true" />
                  {t("admin.invites.dialog.new_brand.trigger")}
                </Button>
              }
            />
          ) : undefined
        }
        toolbar={
          <ListToolbar searchValue={filters.q} onSearchChange={onSearch} />
        }
        footer={
          <ListPagination
            page={pagination.page}
            perPage={pagination.per_page}
            total={pagination.total}
            onPageChange={onPageChange}
          />
        }
      >
        {isEmpty ? (
          hasActiveFilters ? (
            <EmptyState
              data-testid="admin-list-empty"
              title={t("admin.list.empty_filter_brands")}
              actions={
                <Button
                  variant="outline"
                  onClick={onClearFilters}
                  data-testid="admin-list-empty-clear"
                >
                  {t("admin.shared.clear_filters")}
                </Button>
              }
            />
          ) : (
            <EmptyState
              data-testid="admin-list-empty"
              icon={Building2}
              title={t("admin.list.empty_fresh_brands")}
            />
          )
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <SortHeader
                    column="name"
                    label={t("admin.brands_list.column.name")}
                    sort={sort}
                    onToggle={onSortToggle}
                  />
                </TableHead>
                <TableHead>
                  <SortHeader
                    column="created_at"
                    label={t("admin.brands_list.column.created_at")}
                    sort={sort}
                    onToggle={onSortToggle}
                  />
                </TableHead>
                <TableHead className="text-right">
                  {t("admin.brands_list.column.team_size")}
                </TableHead>
                <TableHead>{t("admin.brands_list.column.agency")}</TableHead>
                <TableHead className="text-right">
                  {t("admin.brands_list.column.crew_size")}
                </TableHead>
                <TableHead>
                  <span className="sr-only">
                    {t("admin.brands_list.column.view")}
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {brands.map((brand) => (
                <TableRow
                  key={brand.id}
                  data-testid="admin-list-row"
                  data-brand-id={brand.id}
                >
                  <TableCell className="font-medium">
                    {/*
                     * OKS-13 F7 backport: render `BrandLiveBadge` inline
                     * with the name when the brand is in pending-
                     * activation state (`live === false`). Component is
                     * a no-op for `live: true` so the table reads cleanly
                     * for the silent-norm majority. Same exported symbol
                     * as the agency-side surface — QA contract spec
                     * asserts the import.
                     */}
                    <span className="inline-flex items-center gap-2">
                      <span>{brand.name}</span>
                      <BrandLiveBadge
                        live={brand.live}
                        testId="admin-brand-live-badge"
                      />
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {dateFmt.format(new Date(brand.created_at))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {brand.team_size}
                  </TableCell>
                  <TableCell>
                    {brand.agency ? (
                      brand.agency.name
                    ) : (
                      <span className="text-muted-foreground">
                        {t("admin.brands_list.no_agency")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {brand.crew_size}
                  </TableCell>
                  <TableCell className="text-right">
                    {/*
                     * OKS-312 — Per-row "Act as {Brand}" entry. Inertia
                     * `<Form method="post">` POSTs `{brand_id}` to
                     * `admin_act_as_brand_path`; the row's `can_act_as`
                     * flag (admin-only surface, constant true today)
                     * gates rendering so a future role-tier carve-out
                     * can hide the button without FE changes.
                     */}
                    <div className="flex items-center justify-end gap-2">
                      {brand.can_act_as && (
                        <BrandListActAsButton
                          brand={{ id: brand.id, name: brand.name }}
                        />
                      )}
                      <Link
                        href={adminBrandPath({ id: brand.id })}
                        className="text-foreground hover:text-foreground/80 text-sm font-medium underline-offset-4 hover:underline"
                        aria-label={t("admin.brands_list.view_label", {
                          name: brand.name,
                        })}
                      >
                        {t("admin.brands_list.column.view")}
                      </Link>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ListShell>
    </AppLayout>
  )
}

interface SortHeaderProps {
  column: SortableColumn
  label: string
  sort: { by: string; dir: "asc" | "desc" }
  onToggle: (column: SortableColumn) => void
}

function SortHeader({ column, label, sort, onToggle }: SortHeaderProps) {
  const { t } = useTranslation()
  const isActive = sort.by === column
  const Icon = isActive
    ? sort.dir === "asc"
      ? ArrowUp
      : ArrowDown
    : ArrowUpDown
  return (
    <button
      type="button"
      onClick={() => onToggle(column)}
      className="text-foreground hover:text-foreground/80 inline-flex items-center gap-1 text-xs font-medium tracking-wide uppercase"
      aria-label={t("admin.brands_list.sort.toggle_label", { column: label })}
      aria-sort={
        isActive ? (sort.dir === "asc" ? "ascending" : "descending") : "none"
      }
      data-testid={`admin-list-sort-${column}`}
    >
      <span>{label}</span>
      <Icon className="size-3.5" aria-hidden="true" />
    </button>
  )
}
